// Snake — Endlos-Arena gegen 3 lokale Bots. Nur die Punkte des Spielers
// zählen. Komplett auf Canvas gezeichnet (Voxel-Look, keine Assets).
import { useRef, useEffect } from "react";
import { drawGrassBoard, drawBlockText, drawCoin, setupCanvas } from "./pixelArt";

const ROWS = 26;
const CELL = 20;
const H = ROWS * CELL;
const TICK_MS = 138; // ~0.8x der alten Geschwindigkeit (110 / 0.8)
const APPLE_COUNT = 5;
const BOT_RESPAWN_MS = 3000;

// Spieler = kräftiges Rot, Bots = fast identisches Blau (nur leicht
// unterschiedliche Töne, damit man sie noch auseinanderhalten kann).
const PLAYER_COLORS = { body: "#e23b2f", dark: "#a81f16", head: "#f2564a" };
const BOT_COLORS = [
  { body: "#4bb3e0", dark: "#2f8fbd", head: "#6fc8ee" },
  { body: "#4ba8e8", dark: "#2f86c4", head: "#6fc0f2" },
  { body: "#4bbdd6", dark: "#2f97b0", head: "#6fd2e6" },
];

const DIRS = [
  { x: 1, y: 0 },
  { x: -1, y: 0 },
  { x: 0, y: 1 },
  { x: 0, y: -1 },
];

export default function SnakeGame({ onGameOver, onStart, autoStart = false }) {
  const canvasRef = useRef(null);
  const onGameOverRef = useRef(onGameOver);
  onGameOverRef.current = onGameOver;
  const onStartRef = useRef(onStart);
  onStartRef.current = onStart;

  useEffect(() => {
    const canvas = canvasRef.current;
    // Arena-Breite ans Seitenverhältnis des Containers anpassen: auf dem
    // Desktop wird das Feld breiter (mehr Spalten), die Zellen bleiben
    // quadratisch — nichts wird verzerrt.
    const availW = canvas.parentElement?.clientWidth || ROWS * CELL;
    const availH = canvas.parentElement?.clientHeight || H;
    const aspect = Math.min(Math.max(availW / availH, 1), 3.2);
    const COLS = Math.round(ROWS * aspect);
    const W = COLS * CELL;
    // Mehr Fläche = proportional mehr Äpfel (Basis: 5 auf 26x26)
    const appleCount = Math.max(
      APPLE_COUNT,
      Math.round((COLS * ROWS) / 135)
    );
    const ctx = setupCanvas(canvas, W, H);

    const state = {
      phase: "ready", // ready | running | over
      snakes: [],
      apples: [],
      coin: null, // höchstens eine Münze gleichzeitig auf dem Feld
      score: 0,
      coins: 0,
      lastTick: 0,
    };

    const key = (x, y) => y * COLS + x;

    const occupiedSet = () => {
      const set = new Set();
      for (const s of state.snakes) {
        if (!s.alive) continue;
        for (const seg of s.body) set.add(key(seg.x, seg.y));
      }
      for (const a of state.apples) set.add(key(a.x, a.y));
      if (state.coin) set.add(key(state.coin.x, state.coin.y));
      return set;
    };

    const randomFreeCell = () => {
      const occ = occupiedSet();
      for (let tries = 0; tries < 500; tries++) {
        const x = 2 + Math.floor(Math.random() * (COLS - 4));
        const y = 2 + Math.floor(Math.random() * (ROWS - 4));
        if (!occ.has(key(x, y))) return { x, y };
      }
      return null;
    };

    const spawnApple = () => {
      const cell = randomFreeCell();
      if (cell) state.apples.push(cell);
    };

    const makeSnake = (isPlayer, colors, startX, startY, dir) => ({
      isPlayer,
      colors,
      body: [
        { x: startX, y: startY },
        { x: startX - dir.x, y: startY - dir.y },
        { x: startX - 2 * dir.x, y: startY - 2 * dir.y },
      ],
      dir,
      pendingDir: dir,
      alive: true,
      respawnAt: 0,
      growth: 0,
    });

    const reset = () => {
      state.snakes = [
        makeSnake(true, PLAYER_COLORS, 6, ROWS - 6, { x: 1, y: 0 }),
        makeSnake(false, BOT_COLORS[0], COLS - 7, 5, { x: -1, y: 0 }),
        makeSnake(false, BOT_COLORS[1], 6, 5, { x: 1, y: 0 }),
        makeSnake(false, BOT_COLORS[2], COLS - 7, ROWS - 6, { x: -1, y: 0 }),
      ];
      state.apples = [];
      for (let i = 0; i < appleCount; i++) spawnApple();
      state.coin = null;
      state.score = 0;
      state.coins = 0;
    };

    const inBounds = (x, y) => x >= 0 && x < COLS && y >= 0 && y < ROWS;

    // Bot-KI: gehe Richtung nächster Apfel, aber nur auf freie Felder.
    const chooseBotDir = (snake, blocked) => {
      let target = null;
      let bestDist = Infinity;
      const head = snake.body[0];
      for (const a of state.apples) {
        const d = Math.abs(a.x - head.x) + Math.abs(a.y - head.y);
        if (d < bestDist) {
          bestDist = d;
          target = a;
        }
      }
      const options = DIRS.filter(
        (d) => !(d.x === -snake.dir.x && d.y === -snake.dir.y)
      );
      let best = null;
      let bestScore = -Infinity;
      for (const d of options) {
        const nx = head.x + d.x;
        const ny = head.y + d.y;
        if (!inBounds(nx, ny) || blocked.has(key(nx, ny))) continue;
        let s = Math.random() * 0.5;
        if (d.x === snake.dir.x && d.y === snake.dir.y) s += 0.3;
        if (target) {
          s -= Math.abs(target.x - nx) + Math.abs(target.y - ny);
        }
        if (s > bestScore) {
          bestScore = s;
          best = d;
        }
      }
      return best || snake.dir; // Kein sicherer Zug → Bot läuft in den Tod
    };

    const tick = (now) => {
      // Bots respawnen lassen
      for (const s of state.snakes) {
        if (!s.isPlayer && !s.alive && now >= s.respawnAt) {
          const cell = randomFreeCell();
          if (cell) {
            s.body = [cell];
            s.dir = DIRS[Math.floor(Math.random() * 4)];
            s.pendingDir = s.dir;
            s.growth = 2;
            s.alive = true;
          }
        }
      }

      // Belegte Felder (alle Körper; Schwänze bewegen sich zwar weg,
      // aber die konservative Prüfung hält das Spiel einfach & fair).
      const blocked = new Set();
      for (const s of state.snakes) {
        if (!s.alive) continue;
        for (const seg of s.body) blocked.add(key(seg.x, seg.y));
      }

      // Spieler zuerst bewegen, dann die Bots
      const order = [...state.snakes].sort((a, b) =>
        a.isPlayer === b.isPlayer ? 0 : a.isPlayer ? -1 : 1
      );

      for (const s of order) {
        if (!s.alive) continue;

        if (!s.isPlayer) s.pendingDir = chooseBotDir(s, blocked);
        s.dir = s.pendingDir;

        const head = s.body[0];
        const nx = head.x + s.dir.x;
        const ny = head.y + s.dir.y;

        const willGrow = s.growth > 0;
        const tail = s.body[s.body.length - 1];
        if (!willGrow) blocked.delete(key(tail.x, tail.y));

        const crashed = !inBounds(nx, ny) || blocked.has(key(nx, ny));

        if (crashed) {
          s.alive = false;
          if (s.isPlayer) {
            state.phase = "over";
            if (onGameOverRef.current)
              onGameOverRef.current(state.score, state.coins);
          } else {
            for (const seg of s.body) blocked.delete(key(seg.x, seg.y));
            s.respawnAt = now + BOT_RESPAWN_MS;
          }
          continue;
        }

        s.body.unshift({ x: nx, y: ny });
        blocked.add(key(nx, ny));
        if (willGrow) {
          s.growth--;
        } else {
          s.body.pop();
        }

        // Apfel gefressen?
        const appleIdx = state.apples.findIndex(
          (a) => a.x === nx && a.y === ny
        );
        if (appleIdx !== -1) {
          state.apples.splice(appleIdx, 1);
          s.growth += 2;
          if (s.isPlayer) state.score++;
          spawnApple();
          // Selten taucht zusätzlich eine Münze auf (10 Münzen = 1 gratis Download)
          if (!state.coin && Math.random() < 0.045) {
            state.coin = randomFreeCell();
          }
        }

        // Münze eingesammelt? Nur die des Spielers zählt.
        if (state.coin && state.coin.x === nx && state.coin.y === ny) {
          state.coin = null;
          if (s.isPlayer) state.coins++;
        }
      }
    };

    const drawApple = (a) => {
      const x = a.x * CELL;
      const y = a.y * CELL;
      ctx.fillStyle = "#d93a2b";
      ctx.fillRect(x + 3, y + 5, CELL - 6, CELL - 7);
      ctx.fillStyle = "#f06451";
      ctx.fillRect(x + 5, y + 7, 4, 4);
      ctx.fillStyle = "#5a3d1e";
      ctx.fillRect(x + CELL / 2 - 1, y + 2, 3, 4);
      ctx.fillStyle = "#4e9420";
      ctx.fillRect(x + CELL / 2 + 2, y + 2, 4, 3);
    };

    const drawSnake = (s) => {
      for (let i = s.body.length - 1; i >= 0; i--) {
        const seg = s.body[i];
        const x = seg.x * CELL;
        const y = seg.y * CELL;
        const isHead = i === 0;
        ctx.fillStyle = s.colors.dark;
        ctx.fillRect(x + 1, y + 1, CELL - 2, CELL - 2);
        ctx.fillStyle = isHead ? s.colors.head : s.colors.body;
        ctx.fillRect(x + 2, y + 2, CELL - 4, CELL - 4);
        if (isHead) {
          // Augen quer zur Laufrichtung
          ctx.fillStyle = "#ffffff";
          const e = 4;
          if (s.dir.x !== 0) {
            const ex = s.dir.x > 0 ? x + CELL - 9 : x + 5;
            ctx.fillRect(ex, y + 4, e, e);
            ctx.fillRect(ex, y + CELL - 8, e, e);
            ctx.fillStyle = "#1f2410";
            ctx.fillRect(ex + (s.dir.x > 0 ? 2 : 0), y + 5, 2, 2);
            ctx.fillRect(ex + (s.dir.x > 0 ? 2 : 0), y + CELL - 7, 2, 2);
          } else {
            const ey = s.dir.y > 0 ? y + CELL - 9 : y + 5;
            ctx.fillRect(x + 4, ey, e, e);
            ctx.fillRect(x + CELL - 8, ey, e, e);
            ctx.fillStyle = "#1f2410";
            ctx.fillRect(x + 5, ey + (s.dir.y > 0 ? 2 : 0), 2, 2);
            ctx.fillRect(x + CELL - 7, ey + (s.dir.y > 0 ? 2 : 0), 2, 2);
          }
        }
      }
    };

    const draw = () => {
      drawGrassBoard(ctx, COLS, ROWS, CELL);
      for (const a of state.apples) drawApple(a);
      if (state.coin) {
        drawCoin(ctx, state.coin.x * CELL + 2, state.coin.y * CELL + 2, CELL - 4);
      }
      // Bots zuerst, Spieler oben drüber
      for (const s of state.snakes) {
        if (s.alive && !s.isPlayer) drawSnake(s);
      }
      const player = state.snakes.find((s) => s.isPlayer);
      if (player && player.alive) drawSnake(player);

      // HUD
      drawBlockText(ctx, `${state.score}`, W / 2, 30, 30);
      drawCoin(ctx, 12, 14, 20);
      drawBlockText(ctx, `${state.coins}`, 48, 25, 20);

      if (state.phase === "ready") {
        ctx.fillStyle = "rgba(15, 20, 10, 0.6)";
        ctx.fillRect(0, 0, W, H);
        drawBlockText(ctx, "SNAKE", W / 2, H / 2 - 40, 46);
        drawBlockText(ctx, "3 Bots warten schon...", W / 2, H / 2 + 5, 18);
        drawBlockText(ctx, "Taste drücken / Tippen zum Start", W / 2, H / 2 + 40, 15);
      } else if (state.phase === "over") {
        ctx.fillStyle = "rgba(15, 20, 10, 0.6)";
        ctx.fillRect(0, 0, W, H);
        drawBlockText(ctx, "GAME OVER", W / 2, H / 2 - 40, 40);
        drawBlockText(ctx, `${state.score} Äpfel`, W / 2, H / 2 + 5, 24);
        drawBlockText(ctx, "Leertaste / Tippen für Neustart", W / 2, H / 2 + 45, 15);
      }
    };

    let raf;
    const loop = (now) => {
      if (state.phase === "running" && now - state.lastTick >= TICK_MS) {
        state.lastTick = now;
        tick(now);
      }
      draw();
      raf = requestAnimationFrame(loop);
    };

    const start = () => {
      reset();
      state.phase = "running";
      state.lastTick = performance.now();
      if (onStartRef.current) onStartRef.current();
    };

    const setPlayerDir = (dx, dy) => {
      const player = state.snakes.find((s) => s.isPlayer);
      if (!player || !player.alive) return;
      if (dx === -player.dir.x && dy === -player.dir.y) return;
      player.pendingDir = { x: dx, y: dy };
    };

    const onKeyDown = (e) => {
      const k = e.key;
      const dirKeys = {
        ArrowUp: [0, -1], w: [0, -1], W: [0, -1],
        ArrowDown: [0, 1], s: [0, 1], S: [0, 1],
        ArrowLeft: [-1, 0], a: [-1, 0], A: [-1, 0],
        ArrowRight: [1, 0], d: [1, 0], D: [1, 0],
      };
      if (dirKeys[k]) {
        e.preventDefault();
        if (state.phase === "running") setPlayerDir(...dirKeys[k]);
        else start();
        return;
      }
      if (k === " " || k === "Enter") {
        e.preventDefault();
        if (state.phase !== "running") start();
      }
    };

    // Touch: Wischen = Richtung, Tippen = Start
    let touchStart = null;
    const onTouchStart = (e) => {
      touchStart = e.touches[0];
    };
    const onTouchEnd = (e) => {
      if (!touchStart) return;
      const t = e.changedTouches[0];
      const dx = t.clientX - touchStart.clientX;
      const dy = t.clientY - touchStart.clientY;
      touchStart = null;
      if (state.phase !== "running") {
        start();
        return;
      }
      if (Math.abs(dx) < 20 && Math.abs(dy) < 20) return;
      if (Math.abs(dx) > Math.abs(dy)) setPlayerDir(dx > 0 ? 1 : -1, 0);
      else setPlayerDir(0, dy > 0 ? 1 : -1);
    };
    const onClick = () => {
      if (state.phase !== "running") start();
    };

    window.addEventListener("keydown", onKeyDown);
    canvas.addEventListener("touchstart", onTouchStart, { passive: true });
    canvas.addEventListener("touchend", onTouchEnd, { passive: true });
    canvas.addEventListener("click", onClick);

    if (autoStart) start(); // Start-Button auf der Seite: direkt losspielen
    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("keydown", onKeyDown);
      canvas.removeEventListener("touchstart", onTouchStart);
      canvas.removeEventListener("touchend", onTouchEnd);
      canvas.removeEventListener("click", onClick);
    };
  }, [autoStart]);

  return (
    <canvas
      ref={canvasRef}
      className="w-full h-full touch-none select-none"
      style={{ imageRendering: "pixelated", objectFit: "contain" }}
    />
  );
}
